/**
 * The room: renderer, environment, lights and ground.
 *
 * The look is a bright product-photography set — one key with a soft shadow,
 * a cool rim, and an image-based environment so the gloss has something to
 * catch. Quality is dialled to the device tier rather than fixed.
 */

import * as THREE from 'three';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import type { DeviceTier } from '../lib/device.ts';
import { backdropTexture } from '../textures/decals.ts';

export interface Room {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  key: THREE.DirectionalLight;
  ground: THREE.Mesh;
  dispose(): void;
}

export function createRenderer(canvas: HTMLCanvasElement, tier: DeviceTier): THREE.WebGLRenderer {
  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: tier !== 'low',
    alpha: false,
    powerPreference: 'high-performance',
    stencil: false,
  });
  renderer.setPixelRatio(Math.min(devicePixelRatio, tier === 'low' ? 1.5 : 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.06;
  renderer.shadowMap.enabled = true;
  /* Three 0.186 removed PCFSoftShadowMap; softness now comes from shadow.radius. */
  renderer.shadowMap.type = THREE.PCFShadowMap;
  return renderer;
}

function addLights(scene: THREE.Scene, tier: DeviceTier): THREE.DirectionalLight {
  const key = new THREE.DirectionalLight(0xffffff, 2.15);
  key.position.set(-24, 52, 30);
  key.castShadow = true;
  const size = tier === 'low' ? 1024 : 2048;
  key.shadow.mapSize.set(size, size);
  key.shadow.camera.near = 10;
  key.shadow.camera.far = 160;
  const half = 42;
  Object.assign(key.shadow.camera, { left: -half, right: half, top: half, bottom: -half });
  key.shadow.bias = -0.0006;
  key.shadow.normalBias = 0.035;
  key.shadow.radius = 3;
  scene.add(key, key.target);

  const rim = new THREE.DirectionalLight(0xeaf6ef, 0.65);
  rim.position.set(30, 26, -36);
  scene.add(rim);

  scene.add(new THREE.HemisphereLight(0xffffff, 0xdce7e1, 0.55));
  return key;
}

function addGround(scene: THREE.Scene): THREE.Mesh {
  const ground = new THREE.Mesh(
    new THREE.PlaneGeometry(400, 400),
    new THREE.MeshStandardMaterial({ color: 0xf2f6f4, roughness: 0.92, metalness: 0 }),
  );
  ground.rotation.x = -Math.PI / 2;
  ground.position.y = -0.02;
  ground.receiveShadow = true;
  scene.add(ground);
  return ground;
}

export function createRoom(canvas: HTMLCanvasElement, tier: DeviceTier): Room {
  const renderer = createRenderer(canvas, tier);

  const scene = new THREE.Scene();
  scene.background = backdropTexture();
  scene.backgroundIntensity = 1;

  const pmrem = new THREE.PMREMGenerator(renderer);
  pmrem.compileEquirectangularShader();
  const envRT = pmrem.fromScene(new RoomEnvironment(), 0.035);
  scene.environment = envRT.texture;
  scene.environmentIntensity = 0.92;

  const key = addLights(scene, tier);
  const ground = addGround(scene);

  return {
    renderer,
    scene,
    key,
    ground,
    dispose() {
      envRT.dispose();
      pmrem.dispose();
      renderer.dispose();
    },
  };
}
